import React from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import { LandingPage } from './pages/LandingPage';
import { Dashboard } from './pages/Dashboard';
import { RedirectComponent } from './components/RedirectComponent';
import { SongCatalog } from './pages/SongCatalog';
import { KaraokePlayer } from './pages/KaraokePlayer';
import { AIStudio } from './pages/AIStudio';
import { AICoach } from './pages/AICoach';
import { KODStudio } from './pages/KODStudio';
import { RecordingsPage } from './pages/RecordingsPage';
import { RecordPage } from './pages/RecordPage';
import { AdminDashboard } from './pages/AdminDashboard';
import { AdminSongs } from './pages/AdminSongs';
import { AdminDistribution } from './pages/AdminDistribution';
import { AdminCoins } from './pages/AdminCoins';
import { AdminUsers } from './pages/AdminUsers';
import { AdminTournaments } from './pages/AdminTournaments';
import { AdminCategories } from './pages/AdminCategories';
import { AdminAchievements } from './pages/AdminAchievements';
import AdminPrivacyPolicy from './pages/AdminPrivacyPolicy';
import { Tournaments } from './pages/Tournaments';
import { TournamentDetail } from './pages/TournamentDetail';
import { Academy } from './pages/Academy';
import { Profile } from './pages/Profile';
import { Layout } from './components/Layout';
import { Header } from './components/Header';
import { UserProvider } from './context/UserContext';
import { SongProvider } from './context/SongContext';
import { useState, useEffect } from 'react';
import { Login } from './pages/Auth/Login';
import { Signup } from './pages/Auth/Signup';
import { ResetPassword } from './pages/Auth/ResetPassword';
import { UpdatePassword } from './pages/Auth/UpdatePassword';
import { DataProvider } from './context/DataContext';
import { SharedRecordingPage } from './pages/SharedRecordingPage';
import { PurchaseCoins } from './pages/PurchaseCoins';
import { SongList } from './pages/SongList';
import { AuditionAgreement } from './pages/AuditionAgreement';
import { JocelynScore } from './pages/JocelynScore';
import { AuditionSongs } from './pages/AuditionSongs';
import SharedBracket from './pages/SharedBracket';
import { TechnicalDebug } from './pages/TechnicalDebug';
import { Achievements } from './pages/Achievements';
import { Leaderboard } from './pages/Leaderboard';
import { HelpSupport } from './pages/HelpSupport';
import { PrivacyPolicy } from './pages/PrivacyPolicy';
import { DataDeletion } from './pages/DataDeletion';
import AdminDataDeletion from './pages/AdminDataDeletion';
import { SongLibrary } from './pages/SongLibrary';
import { SingRouter } from './components/SingRouter';
import { Referrals } from './pages/Referrals';
import { AdminReferrals } from './pages/AdminReferrals';
import { AdminKioskLeagues } from './pages/AdminKioskLeagues';
import { AdminKioskLeagueDetail } from './pages/AdminKioskLeagueDetail';
import { AdminSingAlongLeagues } from './pages/AdminSingAlongLeagues';
import { KioskLeagueLanding } from './pages/KioskLeagueLanding';
import { KioskSignup } from './pages/KioskSignup';
import { KioskVerify } from './pages/KioskVerify';
import { KioskMe } from './pages/KioskMe';
import { KioskSing } from './pages/KioskSing';
import { KioskLeaderboard } from './pages/KioskLeaderboard';
import { KioskStorefront } from './pages/KioskStorefront';
import { ContestPlans } from './pages/ContestPlans';
import { ContestSetup } from './pages/ContestSetup';
import { ContestManage } from './pages/ContestManage';
import { ContestDashboard } from './pages/ContestDashboard';
import { KioskShare } from './pages/KioskShare';
import { SingAlongLanding } from './pages/SingAlongLanding';
import { CanYouSingLanding } from './pages/CanYouSingLanding';
import { SingAlongSignup } from './pages/SingAlongSignup';
import { SingAlongPlans } from './pages/SingAlongPlans';
import { SingAlongSetup } from './pages/SingAlongSetup';
import { SingAlongDashboard } from './pages/SingAlongDashboard';
import { SingAlongLeaderboard } from './pages/SingAlongLeaderboard';
import { AdminSingAlongBundles } from './pages/AdminSingAlongBundles';
import { AdminSingAlongBranding } from './pages/AdminSingAlongBranding';
import { AdminVocalBlueprintDebug } from './pages/AdminVocalBlueprintDebug';
import { DeleteAccount } from './pages/DeleteAccount';

const PublicLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <div className="flex flex-col min-h-screen bg-gradient-to-b from-purple-950 to-[#1a0530] text-white">
      <Header />
      <main className="flex-1 overflow-y-auto">{children}</main>
    </div>
  );
};

function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

function App() {
  const [appReady, setAppReady] = useState(false);

  useEffect(() => {
    // Add a small delay to ensure auth state is loaded
    const timer = setTimeout(() => {
      setAppReady(true);
    }, 500);
    
    return () => clearTimeout(timer);
  }, []);

  return (
    <UserProvider>
      <DataProvider>
        <SongProvider>
          {!appReady ? (
            <div className="flex flex-col items-center justify-center min-h-screen bg-gradient-to-b from-purple-950 to-[#1a0530] relative overflow-hidden">
              <div className="absolute inset-0 flex items-center justify-center opacity-5">
                <img
                  src="/TSL_Logo.png"
                  alt="Logo"
                  className="w-[600px] h-[600px] object-contain"
                />
              </div>
              <div className="relative z-10 animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-violet-400"></div>
            </div>
          ) : (
            <>
              <ScrollToTop />
              <Routes>
                <Route path="/" element={<RedirectComponent />} />
                <Route path="/contests" element={<KioskStorefront />} />
                <Route path="/contests/plans" element={<ContestPlans />} />
                <Route path="/contests/setup" element={<ContestSetup />} />
                <Route path="/contests/dashboard" element={<ContestDashboard />} />
                <Route path="/contests/manage" element={<ContestManage />} />
                <Route path="/contests/manage/:leagueId" element={<ContestManage />} />
                <Route path="/league/:slug" element={<KioskLeagueLanding />} />
                <Route path="/league/:slug/leaderboard" element={<KioskLeaderboard />} />
                <Route path="/league/:slug/signup" element={<KioskSignup />} />
                <Route path="/league/:slug/verify" element={<KioskVerify />} />
                <Route path="/league/:slug/me" element={<KioskMe />} />
                <Route path="/league/:slug/sing/:songId" element={<KioskSing />} />
                <Route path="/league/:slug/share/:submissionId" element={<KioskShare />} />
                <Route path="/sing-along" element={<SingAlongLanding />} />
                <Route path="/sing-along/signup" element={<SingAlongSignup />} />
                <Route path="/sing-along/verify" element={<SingAlongSignup />} />
                <Route path="/sing-along/plans" element={<SingAlongPlans />} />
                <Route path="/sing-along/setup" element={<SingAlongSetup />} />
                <Route path="/sing-along/dashboard" element={<SingAlongDashboard />} />
                <Route path="/sing-along/:slug/leaderboard" element={<SingAlongLeaderboard />} />
                <Route path="/sing-along/:slug" element={<KioskLeagueLanding />} />
                <Route path="/sing-along/:slug/signup" element={<KioskSignup />} />
                <Route path="/sing-along/:slug/verify" element={<KioskVerify />} />
                <Route path="/sing-along/:slug/me" element={<KioskMe />} />
                <Route path="/sing-along/:slug/sing/:songId" element={<KioskSing />} />
                <Route path="/sing-along/:slug/share/:submissionId" element={<KioskShare />} />
                <Route path="/can-you-sing" element={<CanYouSingLanding />} />
                <Route path="/can-you-sing/signup" element={<SingAlongSignup />} />
                <Route path="/can-you-sing/verify" element={<SingAlongSignup />} />
                <Route path="/can-you-sing/plans" element={<SingAlongPlans />} />
                <Route path="/can-you-sing/setup" element={<SingAlongSetup />} />
                <Route path="/can-you-sing/dashboard" element={<SingAlongDashboard />} />
                <Route path="/can-you-sing/:slug/leaderboard" element={<SingAlongLeaderboard />} />
                <Route path="/can-you-sing/:slug" element={<KioskLeagueLanding />} />
                <Route path="/can-you-sing/:slug/signup" element={<KioskSignup />} />
                <Route path="/can-you-sing/:slug/verify" element={<KioskVerify />} />
                <Route path="/can-you-sing/:slug/me" element={<KioskMe />} />
                <Route path="/can-you-sing/:slug/sing/:songId" element={<KioskSing />} />
                <Route path="/can-you-sing/:slug/share/:submissionId" element={<KioskShare />} />
                <Route path="/shared/:recordingId" element={<SharedRecordingPage />} />
                <Route path="/bracket/:tournamentId" element={<SharedBracket />} />
                <Route path="/audition-agreement" element={<AuditionAgreement />} />
                <Route path="/audition-songs" element={<AuditionSongs />} />
                <Route path="/privacy" element={<PrivacyPolicy />} />
                <Route path="/data-deletion" element={<DataDeletion />} />
              <Route path="/login" element={<Login />} />
              <Route path="/signup" element={<Signup />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/update-password" element={<UpdatePassword />} />
              <Route path="/public/song-list" element={<SongList />} />
              <Route path="/help" element={<PublicLayout><HelpSupport /></PublicLayout>} />
              <Route element={<Layout />}>
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/sing" element={<SingRouter />} />
                <Route path="/catalog" element={<SongCatalog />} />
                <Route path="/song-library" element={<SongLibrary />} />
                <Route path="/player/:songId" element={<KaraokePlayer />} />
                <Route path="/recordings" element={<RecordingsPage />} />
                <Route path="/record/:songId" element={<RecordPage />} />
                <Route path="/ai-studio" element={<AIStudio />} />
                <Route path="/ai-coach" element={<AICoach />} />
                <Route path="/tournaments" element={<Tournaments />} />
                <Route path="/tournaments/:tournamentId" element={<TournamentDetail />} />
                <Route path="/academy" element={<Academy />} />
                <Route path="/profile" element={<Profile />} />
                <Route path="/delete-account" element={<DeleteAccount />} />
                <Route path="/achievements" element={<Achievements />} />
                <Route path="/leaderboard" element={<Leaderboard />} />
                <Route path="/purchase-coins" element={<PurchaseCoins />} />
                <Route path="/referrals" element={<Referrals />} />
                <Route path="/admin/dashboard" element={<AdminDashboard />} />
                <Route path="/admin/songs" element={<AdminSongs />} />
                <Route path="/admin/distribution" element={<AdminDistribution />} />
                <Route path="/admin/users" element={<AdminUsers />} />
                <Route path="/admin/coins" element={<AdminCoins />} />
                <Route path="/admin/tournaments" element={<AdminTournaments />} />
                <Route path="/admin/categories" element={<AdminCategories />} />
                <Route path="/admin/achievements" element={<AdminAchievements />} />
                <Route path="/admin/privacy-policy" element={<AdminPrivacyPolicy />} />
                <Route path="/admin/data-deletion" element={<AdminDataDeletion />} />
                <Route path="/admin/referrals" element={<AdminReferrals />} />
                <Route path="/admin/kiosk-leagues" element={<AdminKioskLeagues />} />
                <Route path="/admin/kiosk-leagues/:leagueId" element={<AdminKioskLeagueDetail />} />
                <Route path="/admin/sing-along-bundles" element={<AdminSingAlongBundles />} />
                <Route path="/admin/sing-along-branding" element={<AdminSingAlongBranding />} />
                <Route path="/admin/sing-along-leagues" element={<AdminSingAlongLeagues />} />
                <Route path="/admin/vocal-blueprint-debug" element={<AdminVocalBlueprintDebug />} />
                <Route path="/technical/debug" element={<TechnicalDebug />} />
              </Route>
              <Route path="/kod-studio/:songId" element={<KODStudio />} />
              {/* Catch-all route redirects to landing page */}
              <Route path="*" element={<LandingPage />} />
              </Routes>
            </>
          )}
        </SongProvider>
      </DataProvider>
    </UserProvider>
  );
}

export default App;